import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import type { DataUpdateRequest } from '../../../../../shared/dpRequests';
import { ChangeReview } from '../ChangeReview';
import { AttachmentList } from '../AttachmentList';
import { StatusPill } from '../StatusPill';
import { describeRequestStatus } from '../../dpRequests.definitions';
import { useCancelDataUpdate } from '../../hooks/data/useDpRequestMutations';
import { DP_REQUESTS_PATH } from '../../routes';

interface PendingDataUpdatePanelProps {
    request: DataUpdateRequest;
}

export function PendingDataUpdatePanel({ request }: PendingDataUpdatePanelProps) {
    const navigate = useNavigate();
    const cancel = useCancelDataUpdate();
    const [confirming, setConfirming] = useState(false);
    const meta = describeRequestStatus(request);

    async function handleCancel() {
        try {
            await cancel.mutateAsync(request.id);
            navigate(DP_REQUESTS_PATH);
        } catch {
            setConfirming(false);
        }
    }

    return (
        <div className="space-y-6">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
                <div className="flex flex-wrap items-center gap-3">
                    <StatusPill label={meta.label} tone={meta.tone} />
                    <p className="text-sm text-amber-900">
                        Você já tem uma atualização em andamento. Enquanto o DP não responder, não é possível enviar outra.
                    </p>
                </div>
            </div>

            <section className="space-y-3">
                <h3 className="font-montserrat text-base font-semibold text-raro-gray-900">O que você pediu</h3>
                <ChangeReview changes={request.changes} />
                <AttachmentList attachments={request.attachments} />
            </section>

            <div className="flex flex-col-reverse gap-3 border-t border-raro-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-end">
                {confirming ? (
                    <>
                        <p className="mr-auto text-sm text-raro-gray-600">Cancelar este pedido?</p>
                        <Button variant="ghost" onClick={() => setConfirming(false)} disabled={cancel.isPending}>
                            Voltar
                        </Button>
                        <Button variant="danger" onClick={() => void handleCancel()} loading={cancel.isPending}>
                            Sim, cancelar
                        </Button>
                    </>
                ) : (
                    <Button variant="outline" onClick={() => setConfirming(true)}>
                        Cancelar pedido
                    </Button>
                )}
            </div>
        </div>
    );
}
